import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { appConfig } from '../../config/app';
import { CustomEvents } from '../../constants/analytics.constant';
import { useAuthDataContext } from '../../context/auth/auth.provider';
import useLogEventInAnalytics from '../../hooks/useLogAnalyticsEvent';
import SelectEndorsement from '../../screens/PrivateLayout/Endorsements/Endorsements/SelectEndorsement';
import DigitalPolicy from '../../screens/PrivateLayout/Endorsements/Steps/DigitalPolicy/DigitalPolicy';
import FinishStep from '../../screens/PrivateLayout/Endorsements/Steps/FinishStep/FinishStep';
import PatentEngineChassis from '../../screens/PrivateLayout/Endorsements/Steps/PatentEngineChassis/PatentEngineChassis';
import SecuredClause from '../../screens/PrivateLayout/Endorsements/Steps/SecuredClause/SecuredClause';
import SumAssured from '../../screens/PrivateLayout/Endorsements/Steps/SumAssured/SumAssured';
import EndorsementsSummary from '../../screens/PrivateLayout/Endorsements/Steps/Summary/EndorsementsSummary.component';
import {
  createEndorsementAPI,
  finishEndorsement,
} from '../../services/policy/policy.service';
import EndorsementReducer, {
  CLEAN_ENDORSEMENT_BODY,
  FAILURE_ENDORSEMENT,
  REQUEST_ENDORSEMENT,
  SET_LOADER_HELPER,
  SUCCESS_ENDORSEMENT,
  UPDATE_ENDORSEMENT_BODY,
} from './endorsements.reducer';

export const EndorsementsDataContext = createContext<any>(null);

const initialValues = {
  success: {},
  errors: {},
  endorsement_body: {},
  loading: false,
};

export const ENDORSEMENTS: any = {
  vehicle: {
    title: 'Patente, motor y chasis',
    // order: 1,
    content: <PatentEngineChassis></PatentEngineChassis>,
  },
  digital_policy: {
    title: 'Póliza Digital',
    // order: 2,
    content: <DigitalPolicy></DigitalPolicy>,
  },
  sum_assured: {
    title: 'Suma asegurada',
    // order: 2,
    content: <SumAssured></SumAssured>,
  },
  secured_clause: {
    title: 'Cláusula',
    // order: 4,
    content: <SecuredClause></SecuredClause>,
  },
  assistance: {
    title: 'Asistencias',
    // order: 5,
  },
  add_driver: {
    title: 'Agregar conductor',
    order: 6,
  },
  accesories: {
    title: 'Accesorios',
    order: 7,
  },
};

export const ENDORSEMENTS_STEPS = [
  {
    title: 'Modificar póliza',
    order: 1,
    content: <SelectEndorsement></SelectEndorsement>,
  },
  {
    title: 'Resumen',
    content: <EndorsementsSummary></EndorsementsSummary>,
  },
  {
    title: '',
    content: <FinishStep></FinishStep>,
  },
];

const EndorsementsDataProvider = ({ ...props }) => {
  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(EndorsementReducer, initialValues);
  const [open, setOpen] = useState(false);
  const [selectedEndorsements, setSelectedEndorsements] = useState<any[]>([]);
  const history = useHistory();
  const { logEvent } = useLogEventInAnalytics();

  const nextStep = () => {
    setStep(step + 1);
  };

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const goBack = () => {
    if (step === ENDORSEMENTS_STEPS.length - 1) {
      redirectHome();
      cleanEndorsementBody();
    } else {
      setStep(step > 0 ? step - 1 : 0);
    }
  };

  const onSelectedEndorsement = (endorsement: string, isSelected: boolean) => {
    if (ENDORSEMENTS[endorsement]?.content) {
      if (ENDORSEMENTS_STEPS[1]?.title !== 'Resumen') {
        ENDORSEMENTS_STEPS.splice(1, 1);
      }
      setSelectedEndorsements((sel: any) => {
        // Cambiar comportamiendo si se quiere multiples endosos
        if (isSelected) {
          sel.push(ENDORSEMENTS[endorsement]);
          // sel.sort((a: any, b: any) => a.order - b.order);
          if (ENDORSEMENTS_STEPS.length > 3) {
            ENDORSEMENTS_STEPS.splice(1, 1, ENDORSEMENTS[endorsement]);
          } else {
            ENDORSEMENTS_STEPS.splice(1, 0, ENDORSEMENTS[endorsement]);
          }
        }
        return sel;
      });
    }
  };

  const updateEndorsementBody = (body: any) => {
    dispatch({
      type: UPDATE_ENDORSEMENT_BODY,
      payload: body,
    });
  };

  const setLoaderHelper = (isLoading: any) => {
    dispatch({
      type: SET_LOADER_HELPER,
      payload: isLoading,
    });
  };

  const cleanEndorsementBody = () => {
    dispatch({
      type: CLEAN_ENDORSEMENT_BODY,
    });
    setStep(0);
    ENDORSEMENTS_STEPS.splice(1, selectedEndorsements.length);
    setSelectedEndorsements([]);
  };

  const createEndorsement = async (
    vehicle_id: any,
    policy_id: any,
    license_plate: string,
    endorsement_type?: string,
    job_number?: number
  ) => {
    dispatch({
      type: REQUEST_ENDORSEMENT,
    });
    setLoaderHelper(true);

    let body = {
      ...state.endorsement_body,
      vehicle_id,
    };

    if (body.gw_digital_email && Object.keys(body).length <= 2) {
      body = {
        ...body,
        license_plate,
      };
    }

    try {
      // añadir aqui clausula de ajuste
      if (endorsement_type === 'sumInsured') {
        const res = await finishEndorsement(policy_id, job_number);
        setLoaderHelper(false);
        dispatch({
          type: SUCCESS_ENDORSEMENT,
          payload: { ...res.data, http_code: res.status },
        });
      } else {
        delete body.policyInfo;
        delete body.sumInsuredData;
        if (!body.license_plate) {
          body = {
            ...body,
            license_plate,
          };
        }
        const res = await createEndorsementAPI(policy_id, body);
        for (const key in state.endorsement_body) {
          if (
            Object.prototype.hasOwnProperty.call(state.endorsement_body, key)
          ) {
            if (key !== 'vehicle_id') {
              await logEvent(CustomEvents.ENDORSEMENTS_BASE + key, {
                app: appConfig?.NAME,
              });
            }
          }
        }
        setLoaderHelper(false);
        dispatch({
          type: SUCCESS_ENDORSEMENT,
          payload: { ...res.data, http_code: res.status },
        });
      }
    } catch (err) {
      const data = {
        ...err.response?.data,
        http_code: err.response?.status,
      };
      setLoaderHelper(false);
      dispatch({
        type: FAILURE_ENDORSEMENT,
        payload: data,
      });
    }
  };

  const assitanceDataValue = useMemo(
    () => ({
      ...state,
      open,
      setOpen,
      step,
      setStep,
      goBack,
      nextStep,
      updateEndorsementBody,
      cleanEndorsementBody,
      createEndorsement,
      onSelectedEndorsement,
      setLoaderHelper,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, open, state]
  );

  return (
    <EndorsementsDataContext.Provider value={assitanceDataValue} {...props} />
  );
};

export const useEndorsementsDataContext = () =>
  useContext(EndorsementsDataContext);

export default EndorsementsDataProvider;
